import { useState, useCallback, useEffect, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { ActionPrompt, ActionPromptListResponse } from "./actionPromptTypes";

export function useActionPromptsCatalog() {
  const [prompts, setPrompts] = useState<ActionPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const loadAll = useCallback(async () => {
    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    try {
      const all: ActionPrompt[] = [];
      let page = 1;
      const pageSize = 100;
      let total = 0;

      for (;;) {
        const res = await apiFetch(
          `/faceswap/action-prompts?page=${page}&pageSize=${pageSize}`
        );
        if (!res.ok) throw new Error(await res.text());
        // Stale response race protection: ignore if a newer reload has started
        if (requestId !== activeRequestIdRef.current) return;

        const data: ActionPromptListResponse = await res.json();
        const items = data.items || [];
        all.push(...items);
        total = data.total;

        if (all.length >= total) break;
        if (items.length === 0) {
          throw new Error(
            `Action prompt pagination stopped at ${all.length}/${total}`
          );
        }
        page += 1;
      }

      if (requestId === activeRequestIdRef.current) {
        setPrompts(all);
      }
    } catch (err) {
      if (requestId === activeRequestIdRef.current) {
        console.error("Failed to load action prompts catalog:", err);
      }
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  return { prompts, loading, reload: loadAll };
}
